<template>
  <div class="onboarding-down-payment-bg">
    <div v-show="payLoading" class="onboarding-down-payment--container" v-if="transaction">
      <div class="prclx-logo">
        <asset-v3
          dir="logos"
          name="logo.svg"
        />
      </div>
    <loading-page
      ref="loading-page"
    />
    </div>
    <div v-show="!payLoading" class="onboarding-down-payment--container" v-if="transaction">
      <div class="prclx-logo">
        <asset-v3
          dir="logos"
          name="logo.svg"
        />
      </div>

      <h3 class="gray-900">Pagamento da entrada</h3>
      <p class="mt-12 body-regular gray-900">
        Agora só falta pagar o valor da entrada para finalizar sua compra.
      </p>

      <div class="down-payment--info">
        <div class="down-payment--info--column">
          <p class="small-regular gray-900">Valor</p>
          <p class="body-semi-bold gray-900">{{ transaction.downPaymentValue | moneyFilter }}</p>
        </div>

        <div class="down-payment--info--column">
          <p class="small-regular gray-900">Vencimento</p>
          <p class="body-semi-bold gray-900">1 dia útil</p>
        </div>
      </div>

      <div class="down-payment--options">
        <div class="down-payment--option__pix">
          <label class="down-payment--option--container"
            :class="{ 'down-payment--option--container--active': downPaymentOption === 'pix' }">
            <input
              name="down-payment-option"
              type="radio"
              :checked="downPaymentOption === 'pix'"
              @click="() => handleChangeDownPaymentOption('pix')"
            >
            <div class="down-payment--option--info">
              <asset-v3
                dir="icons"
                name="pix-icon.svg"
                :mirrored=false
              />
              <p>Pix</p>
            </div>
            <span class="checkmark"></span>
          </label>
        </div>

        <div class="down-payment--option__boleto">
          <label class="down-payment--option--container"
          :class="{ 'down-payment--option--container--active': downPaymentOption === 'billet' }">
            <input
              name="down-payment-option"
              type="radio"
              :checked="downPaymentOption === 'billet'"
              @click="() => handleChangeDownPaymentOption('billet')"
            >
            <div class="down-payment--option--info">
              <asset-v3
                dir="icons"
                name="boleto-icon.svg"
                :mirrored=false
              />
              <p>Boleto</p>
            </div>
            <span class="checkmark"></span>
          </label>
        </div>

        <div class="down-payment--option__credit-card">
          <label class="down-payment--option--container"
          :class="{
            'down-payment--option--container--active': downPaymentOption === 'credit-card'
          }">
            <input
              name="down-payment-option"
              type="radio"
              :checked="downPaymentOption === 'credit-card'"
              @click="() => handleChangeDownPaymentOption('credit-card')"
            >
            <div class="down-payment--option--info">
              <asset-v3
                dir="icons"
                name="credit-card-small-black.svg"
                :mirrored=false
              />
              <p>Cartão</p>
            </div>
            <span class="checkmark"></span>
          </label>
        </div>
      </div>

      <div v-if="downPaymentOption === 'pix'" class="mt-24">
        <div class="pix--steps">
          <QRCode value="pixCode" :margin="0" class="only-on-large" />
          <div>
            <div class="pix--step">
              <div class="step--number">1</div>
              Abra seu aplicativo de pagamentos.
            </div>
            <div class="pix--step mt-20 mb-20">
              <div class="step--number">2</div>
              Escolha pagar com Pix.
            </div>
            <div class="pix--step">
              <div class="step--number">3</div>
              Copie e cole o código para realizar o pagamento.
            </div>
          </div>
        </div>

        <div style="display: flex; justify-content: center;" v-if="isSmall">
          <v-popover
          class="button"
          :offset="16" placement="bottom" :show="!tooltipDisabled" :autoHide="true">
            <round-button
              class="primary mt-24 hide-on-large"
              style="min-width: 100%"
              @click="copyPixCode"
              content="Copiar código Pix"
            />
            <template slot="popover">
              <div class="tooltip-arrow"></div>
              <div class="tooltip-text">Código copiado</div>
            </template>
          </v-popover>
        </div>

        <copy-row
          class="only-on-large mt-24"
          :content="transaction.downPaymentBillet.pixCode"
        />

        <p class="footer--message">
          Você será avisado assim que o pagamento for confirmado. A confirmação pode demorar até 1
          dia útil após a realização do pagamento.
        </p>
      </div>

      <div v-if="downPaymentOption === 'billet'" class="mt-24">
        <p class="small-regular gray-500 mt-24 hide-on-large">
          Copie e cole o código para pagamento online.
        </p>
        <p class="small-regular gray-500 mt-24 only-on-large">
          Escaneie ou copie e cole o código de barras para pagamento online.
        </p>

        <p class="billet--barcode-number hide-on-large">
          {{ transaction.downPaymentBillet.payNumber }}
        </p>

        <VueBarcode
          class="barcode only-on-large"
          tag="svg"
          format="ITF"
          lineColor="#000"
          :value="transaction.downPaymentBillet.billetDetails.barcodeNumber"
          :displayValue="false"
          :fontSize="40"
          :marginLeft="0"
          :width="1"
        />

        <copy-row
          class="only-on-large mt-24"
          :content="transaction.downPaymentBillet.payNumber"
        />

        <div style="display: flex; justify-content: center;" v-if="isSmall">
          <v-popover
          class="button"
          :offset="16" placement="bottom" :show="!tooltipDisabled" :autoHide="true">
            <round-button
              class="primary mt-24 hide-on-large"
              style="min-width: 100%"
              @click="copyBilletBarCode"
              content="Copiar código"
            />
            <template slot="popover">
              <div class="tooltip-arrow"></div>
              <div class="tooltip-text">Código copiado</div>
            </template>
          </v-popover>
        </div>

        <round-button
          mode="semi-primary hide-on-large"
          class="mt-20"
          @click="openBilletLink"
          content="Visualizar boleto"
        />

        <p class="footer--message">
          Você será avisado assim que o pagamento for confirmado. A confirmação pode demorar até 2
          dias úteis após a realização do pagamento.<br><br>
          Caso o pagamento não seja confirmado em até 3 dias úteis após o vencimento, o boleto será
          cancelado juntamente com o pedido.
        </p>
      </div>

      <div class="credit" v-else-if="downPaymentOption === 'credit-card'">
        <form class="credit-form" @submit.prevent="createCreditCardPaymentToken">
          <div style="display: flex; flex-direction: row; position: relative; width: 100%;">
            <Input
              label="Número do cartão"
              v-model="creditCard.number"
              hasIcon
              inputmode="numeric"
              @blur="cleanErrors('number')"
              @input="getCreditCardLogo"
              class="credit-form-input"
              :errorMessage="validate.number"
              v-mask="'#### #### #### ####'"
            />
            <img
              :src="cardLogo"
              style="position: absolute; left: 12px; width: 24px; height: 24px;"
              :style="{ top: isMobile() ? '36px' : '32px' }"
            />
          </div>
          <Input
            label="Nome impresso no cartão"
            v-model="creditCard.full_name"
            @blur="cleanErrors('name')"
            class="credit-form-input name"
            :errorMessage="validate.name"
          />
          <div style="display: flex; gap: 20px">
            <Input
              label="Validade"
              v-mask="'##/####'"
              style="margin-bottom: 0;"
              inputmode="numeric"
              placeholder="MM/YYYY"
              @blur="cleanErrors('expiration')"
              v-model="creditCard.expiration"
              :errorMessage="validate.expiration"
              />
            <div style="display: flex; flex-direction: row; position: relative; width: 100%;">
              <Input
                label="Cód. segurança"
                @blur="cleanErrors('verification_value')"
                style="margin-bottom: 0;"
                inputmode="numeric"
                labelWrap
                hasIconRight
                v-model="creditCard.verification_value"
                :errorMessage="validate.verification_value"
              />

              <v-popover
                style="position: absolute;
                right: 10px;"
                :style="{ top: isMobile() ? '36px' : '32px' }"
                :offset="16"
                placement="top"
                :show="!tooltipDisabled"
                :autoHide="true"
              >
                <button
                  type="button"
                  class="tooltipButton"
                  @click="showCVVTooltip()"
                >
                  <QuestionMarkCircleIcon />
                </button>
              <template slot="popover">
                <div class="tooltip-arrow-cvv"></div>
                <div class="tooltip-text-cvv">
                  O código de segurança (CVV) é composto por 3 ou 4 números e fica geralmente na
                  parte de trás do cartão.
                </div>
              </template>
            </v-popover>
            </div>
          </div>
          <!-- <Toggle
            v-if="isMobile()"
            v-model="saveCardToken"
            label="Salvar dados do cartão para pagamentos futuros"
          />
          <CheckBox
            v-else
            style="font-family: Usual;
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            line-height: 24px;"
            v-model="saveCardToken"
            label="Salvar dados do cartão para pagamentos futuros"
          /> -->
          <UIButton type="submit" :disabled="payLoading" class="primary pay-button">
            Pagar {{ transaction.downPaymentValue | moneyFilter }}
          </UIButton>
        </form>
      </div>

      <security-warning class="mt-12 pb-12 hide-on-large" />
    </div>
    <security-warning class="pb-32 only-on-large" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { mask } from 'vue-the-mask';
import VueBarcode from 'vue-barcode';
import AssetV3 from '@/components/common/AssetV3.vue';
import QRCode from '@/components/common/QRCode.vue';
import Input from '@/components/ui/AppInput.vue';
import UtilMixin from '@/mixins/util';
import StringMixin from '@/mixins/string';
import { QuestionMarkCircleIcon } from '@vue-hero-icons/outline';
import VisaIcon from '@/feature/onboarding/components/assets/visa.png';
import AmexIcon from '@/feature/onboarding/components/assets/amex.png';
import MasterCardIcon from '@/feature/onboarding/components/assets/mastercard.png';
import DinersIcon from '@/feature/onboarding/components/assets/diners.png';
import GenericIcon from '@/feature/onboarding/components/assets/generic.svg';
import CopyRow from '@/components/common/CopyRow.vue';
import UIButton from '@/components/ui/AppButton.vue';
import LoadingPage from '@/feature/paymentLink/components/LoadingPage.vue';
import RoundButton from '../../paymentLink/components/RoundButton.vue';
import SecurityWarning from '../../paymentLink/components/SecurityWarning.vue';

export default {
  name: 'v2-onboarding-down-payment',
  components: {
    AssetV3,
    VueBarcode,
    QRCode,
    CopyRow,
    RoundButton,
    SecurityWarning,
    QuestionMarkCircleIcon,
    UIButton,
    LoadingPage,
    Input,
  },
  directives: { mask },
  mixins: [StringMixin, UtilMixin],
  data: () => ({
    downPaymentOption: 'pix',
    tooltipDisabled: true,
    creditCard: {
      number: '',
      verification_value: '',
      expiration: '',
      first_name: '',
      last_name: '',
      full_name: '',
    },
    tooltipDisabledCVV: false,
    validate: {
      number: '',
      name: '',
      verification_value: '',
      expiration: '',
    },
    saveCardToken: false,
    creditCardDisabled: true,
    payLoading: false,
    cardLogo: GenericIcon,
  }),
  computed: {
    ...mapState('paymentLink', ['nextRoute']),
    ...mapState('onboardingV2', ['transaction', 'routeParams', 'selfieFlow', 'extraQueryRoute']),
    creditCardFlag() {
      return this.$window.Iugu?.utils.getBrandByCreditCardNumber(this.creditCard.number);
    },
  },
  methods: {
    ...mapActions('onboarding', ['createIuguUser', 'payWithCreditCard']),
    handleChangeDownPaymentOption(paymentOption) {
      this.downPaymentOption = paymentOption;
    },
    isSmall() {
      return this.isMobile();
    },
    copyPixCode() {
      const { pixCode } = this.transaction.downPaymentBillet;
      navigator.clipboard.writeText(pixCode);
    },
    showCVVTooltip() {
      if (this.tooltipDisabledCVV) {
        this.tooltipDisabledCVV = false;
      } else {
        this.tooltipDisabledCVV = true;
      }
    },
    removeMask(value) {
      return value.split('.').join().split(' ').join()
        .replaceAll(',', '');
    },
    copyBilletBarCode() {
      const bardCodeNumber = this.transaction.downPaymentBillet.payNumber;
      navigator.clipboard.writeText(bardCodeNumber);
    },
    openBilletLink() {
      const { installmentLink } = this.transaction.downPaymentBillet;
      window.open(installmentLink);
    },
    getCreditCardLogo() {
      this.cardLogo = GenericIcon;
      if (this.creditCardFlag === 'visa') {
        this.cardLogo = VisaIcon;
      }

      if (this.creditCardFlag === 'mastercard') {
        this.cardLogo = MasterCardIcon;
      }

      if (this.creditCardFlag === 'amex') {
        this.cardLogo = AmexIcon;
      }

      if (this.creditCardFlag === 'diners') {
        this.cardLogo = DinersIcon;
      }
    },
    cleanErrors(type) {
      if (type === 'number' && this.creditCard?.number?.length !== 0) {
        this.getCreditCardLogo();
      }

      if (type === 'name' && this.validate.name.length > 0 && this.creditCard.full_name.length !== 0) {
        this.validate.name = '';
      }

      if (type === 'expiration' && this.validate.expiration.length > 0 && this.creditCard.expiration.length !== 0) {
        this.validate.expiration = '';
      }

      if (type === 'verification_value' && this.validate.verification_value.length > 0 && this.creditCard.verification_value.length !== 0) {
        this.validate.verification_value = '';
      }
    },
    validateCardValues() {
      if (this.creditCard.full_name.length === 0) {
        this.validate.name = 'Nome associado ao cartão é obrigatório';
      } else {
        this.validate.name = '';
        this.creditCard.full_name = this.creditCard.full_name.toUpperCase();
        this.creditCard.first_name = this.creditCard.full_name.split(' ').slice(0, -1).join(' ');
        this.creditCard.last_name = this.creditCard.full_name.split(' ').slice(-1).join(' ');
      }

      if (this.creditCard.first_name.length <= 0 && this.creditCard.last_name.length <= 0) {
        this.validate.name = 'Campo obrigatório';
      } else if (this.creditCard.first_name.length <= 0
        || this.creditCard.last_name.length <= 0) {
        this.validate.name = 'Insira nome e sobrenome';
      }

      if (!this.$window.Iugu.utils.validateCreditCardNumber(this.creditCard.number)) {
        this.validate.number = 'Número de cartão não reconhecido';
      } else {
        this.validate.number = '';
      }

      if (this.creditCard.number.length <= 0) {
        this.validate.number = 'Campo obrigatório';
      }

      if (this.creditCard.expiration.length > 0 && !this.$window.Iugu
        .utils.validateCVV(this.creditCard.verification_value, this.creditCardFlag)) {
        this.validate.verification_value = 'Código de verificação invalido';
      } else {
        this.validate.verification_value = '';
      }

      if (!this.$window.Iugu
        .utils.validateCVV(this.creditCard.verification_value, this.creditCardFlag)) {
        this.validate.verification_value = 'Código de verificação invalido';
      } else {
        this.validate.verification_value = '';
      }

      if (this.creditCard.verification_value.length <= 0) {
        this.validate.verification_value = 'Campo obrigatório';
      }

      if (!this.$window.Iugu.utils.validateExpirationString(this.creditCard.expiration)) {
        this.validate.expiration = 'Data de expiração invalida';
      } else {
        this.validate.expiration = '';
      }

      if (this.creditCard.expiration.length <= 0) {
        this.validate.expiration = 'Campo obrigatório';
      }
    },
    async createCreditCardPaymentToken() {
      this.validateCardValues();
      this.payLoading = true;
      const month = this.creditCard.expiration.replace(/\//g, '').slice(0, 2);
      const year = this.creditCard.expiration.replace(/\//g, '').slice(2, 6);
      const CreditCard = await this.$window.Iugu.CreditCard(
        this.creditCard.number.replace(/\D/g, ''),
        month.toString(),
        year.toString(),
        this.creditCard.first_name.toUpperCase(),
        this.creditCard.last_name.toUpperCase(),
        this.creditCard.verification_value.replace(/\D/g, ''),
      );
      this.$window.Iugu.createPaymentToken(CreditCard, async (response) => {
        if (response.errors) {
          this.$notify({
            text: response.errors.record_invalid,
            type: 'error',
            duration: 5000,
          });
          this.payLoading = false;
        } else {
          try {
            await this.payWithCreditCard({
              userId: this.transaction.userId,
              transactionId: this.transaction._id,
              isPaymentMethodSaved: this.saveCardToken,
              token: response.id,
            }).then((paymentResponse) => {
              if (!paymentResponse.data.success) {
                this.payLoading = false;
                this.$notify({
                  text: paymentResponse.data.errors?.base
                    ? paymentResponse.data.errors.base[0]
                    : paymentResponse.data.message,
                  type: 'error',
                  duration: 5000,
                });
              } else if (paymentResponse.data.success) {
                this.$router.replace({
                  name: 'v2-onboarding-complete',
                  query: {
                    ...this.$route.query,
                    ...this.extraQueryRoute,
                  },
                });
              }
            });
          } catch (error) {
            this.payLoading = false;
            setTimeout(() => {
              this.$notify({
                text: error.response || error.message,
                type: 'error',
                duration: 5000,
              });
            }, 100);
          }
        }
      });
    },
  },
  mounted() {
    const isHomolog = window.location.host.includes('-homolog');
    this.$loadScript('https://js.iugu.com/v2')
      .then(() => {
        this.$window.Iugu.setAccountID('ADF87B047F8F440D9083DD8167854F1A');
        if (isHomolog) {
          this.$window.Iugu.setTestMode(true);
        }
        this.$window.Iugu.setup();
      })
      .catch(() => {
        this.creditCardDisabled = true;
      });
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../scss/__new-styles.scss';

  .tooltip {
    display: block !important;
    z-index: 2;

    .tooltip-text {
      color: white;
      font-family: Usual;
      background: #0F172A;
      border-radius: 6px;
      padding: 12px;
      text-align: center;
    }

    .tooltip-text-cvv {
      color: white;
      font-family: Usual;
      background: #0F172A;
      border-radius: 6px;
      padding: 12px;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      position: absolute;
      top: -84px;
      left: -305px;
      width: 327px;
      line-height: 20px;

      @media (max-width: 406px) {
        position: absolute;
        top: -84px;
        left: -278px;
        width: 300px;
      }

      @media (max-width: 350px) {
        position: absolute;
        top: -104px;
        left: -230px;
        width: 250px;
      }
    }

    .tooltip-arrow {
      width: 0;
      height: 0;
      border-style: solid;
      position: absolute;
      margin: 1px;
      border-color: #0F172A;
      z-index: 1;
    }

    .tooltip-arrow-cvv {
      width: 0;
      height: 0;
      border-style: solid;
      position: absolute;
      margin: 1px;
      border-color: #0F172A;
      z-index: 1;
    }

    &[x-placement^="top"] {
      margin-bottom: 5px;

      .tooltip-arrow {
        border-width: 5px 5px 0 5px;
        border-left-color: transparent !important;
        border-right-color: transparent !important;
        border-bottom-color: transparent !important;
        bottom: -5px;
        left: calc(50% - 5px);
        margin-top: 0;
        margin-bottom: 0;
      }

      .tooltip-arrow-cvv {
        border-width: 5px 5px 0 5px;
        border-left-color: transparent !important;
        border-right-color: transparent !important;
        border-bottom-color: transparent !important;
        bottom: -5px;
        left: calc(9% - 5px);
        margin-top: 0;
        margin-bottom: 0;
      }
    }

    &[x-placement^="bottom"] {
      margin-top: 5px;

      .tooltip-arrow {
        border-width: 0 5px 5px 5px;
        border-left-color: transparent !important;
        border-right-color: transparent !important;
        border-top-color: transparent !important;
        top: -5px;
        left: calc(50% - 5px);
        margin-top: 0;
        margin-bottom: 0;
      }
    }

    &[x-placement^="right"] {
      margin-left: 5px;

      .tooltip-arrow {
        border-width: 5px 5px 5px 0;
        border-left-color: transparent !important;
        border-top-color: transparent !important;
        border-bottom-color: transparent !important;
        left: -5px;
        top: calc(50% - 5px);
        margin-left: 0;
        margin-right: 0;
      }
    }

    &[x-placement^="left"] {
      margin-right: 5px;

      .tooltip-arrow {
        border-width: 5px 0 5px 5px;
        border-top-color: transparent !important;
        border-right-color: transparent !important;
        border-bottom-color: transparent !important;
        right: -5px;
        top: calc(50% - 5px);
        margin-left: 0;
        margin-right: 0;
      }
    }

    &[aria-hidden="true"] {
      visibility: hidden;
      opacity: 0;
      transition: opacity 0.15s, visibility 0.15s;
    }

    &[aria-hidden="false"] {
      visibility: visible;
      opacity: 1;
      transition: opacity 0.15s;
    }
  }
  .tooltipButton {
    display: flex;
    align-items: center;
    border: none !important;
    outline: none !important;
    background: transparent;
    padding: 0;
    width: 24px;
    height: 24px;
    color: #94A3B8;
  }
  .onboarding-down-payment-bg {
    width: 100%;
    min-height: 100vh;

    @media (min-width: $breakpoint-large) {
      background-color: $gray-50;
      padding-top: 22px;
      padding-bottom: 1px;
    }
  }

  .onboarding-down-payment--container {
    background-color: $white;
    box-sizing: border-box;
    width: 100%;
    min-height: 100%;
    padding: 20px 24px;

    @media (min-width: $breakpoint-large) {
      margin: 0px auto 20px auto;
      max-width: 512px;
      border: 1px solid $gray-300;
      border-radius: 12px;
      box-shadow:
        0px 2px 4px -2px rgba(24, 39, 75, 0.12),
        0px 4px 4px -2px rgba(24, 39, 75, 0.08);
      min-height: auto;
    }
  }

  .prclx-logo {
    padding-bottom: 24px;

    @media (min-width: $breakpoint-large) {
      text-align: center;
    }
  }

  .down-payment--info {
    margin: 20px 0px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .down-payment--info--column {
    flex: 1 1 0px;
  }

  .down-payment--option--container {
    flex-grow: 1;
    -webkit-tap-highlight-color: transparent;
    height: 100%;
    background-color: $white;
    border: 1px solid $gray-200;
    border-radius: 6px;
    padding: 12px;
    cursor: pointer;

    display: flex;
    align-items: flex-start;
    justify-content: space-between;

    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &:hover {
      border: 1px solid $primary-300;
    }

    input {
      display: none;
      opacity: 0;
      cursor: pointer;
    }
  }

  .down-payment--option--container--active {
    border: 1px solid $primary-300;
    background-color: $primary-50;
  }

  .down-payment--options {
    display: flex;
    gap: 12px;
  }

  .down-payment--option__pix, .down-payment--option__boleto, .down-payment--option__credit-card {
    width: 100%;
  }

  .down-payment--option--info {
    display: flex;
    gap: 8px;
    justify-content: flex-start;
    flex-direction: column;
    p {
      color: $gray-900;
      line-height: 24px;
      font-size: 16px;
      font-weight: 400;
      font-family: 'Usual';
    }

    img {
      height: 24px;
      width: 24px;
    }

  }

  .checkmark {
    width: 20px;
    height: 20px;
    background-color: $gray-50;
    border-radius: 50%;
    border: 1px solid $gray-300;
  }

  input:checked ~ .checkmark {
    background-color: $white;
    border: 5px solid $primary-500;
  }

  .pix--steps {
    @media (min-width: $breakpoint-large) {
      display: flex;
      gap: 35px;

      img {
        width: 180px;
        height: 180px;
      }
    }
  }

  .pix--step {
    @extend .small-regular;
    color: $gray-500;

    display: flex;
    gap: 8px;

    .step--number {
      @extend .body-semi-bold;
      color: $gray-500;
      background-color: $gray-200;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;

      width: 24px;
      min-width: 24px;
      height: 24px;
      min-height: 24px;
    }
  }

  .billet--barcode-number {
    @extend .body-regular;
    @extend .gray-900;

    margin-top: 12px;
    word-wrap: break-word;
  }

  .barcode {
    text-align: center;
    width: 100%;
  }

  .credit {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 20px;
  }

  .credit>.credit-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }

  @media (min-width: $md) {
    .credit>.credit-form {
      align-items: flex-start;
    }
  }

  .credit>.credit-form .credit-form-input {
    width: 100%;
    max-width: 600px;
    margin-bottom: 0px;
    flex-direction: column;

    &.name ::v-deep input {
      text-transform: uppercase
    }
  }

  .credit>.credit-form>.pay-button {
    width: 100%;
    height: 48px;
  }

  .credit>.credit-title {
    font-family: Usual;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    font-weight: 700;
    line-height: 24px;
  }

  .card-flag {
    display: grid;
    grid-template-columns: 10% 50% 40%;
    padding: 12px 16px;
    align-items: center;
    position: relative;
  }

  .card-flag .name {
    font-size: 16px;
    line-height: 24px;
    color: #212b36;
  }

  .card-flag .expirationDate {
    font-size: 14px;
    line-height: 20px;
    color: #637381;
  }

  .radio-card {
    position: absolute;
    width: 20px;
    display: flex !important;
    height: 100%;
    margin: unset;
    right: 20px;
    align-items: center;
  }

  .v-popover {
    display: block !important;
  }

  .v-popover.button, .v-popover.button ::v-deep .trigger {
    width: 100%;
  }

  .footer--message {
    @extend .small-regular;
    @extend .gray-500;

    margin-top: 20px;

    @media (min-width: $breakpoint-large) {
      @include x-small-regular;
    }
  }
</style>
